import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { PartnerDashboardDetailsProps, PartnerDashboardProps } from '@/models/PartnerDashboard';
import { PartnerGroupSelectModel } from '@/models/PartnerGroupSelectModel';

type Props = {
  dashboardPartner: PartnerDashboardDetailsProps;
  partnerModalOpen: boolean;
  setPartnerModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  theme: Theme;
  handleRowUpdate: (updated: PartnerDashboardProps) => void;
};

export default function PartnerModal({
  dashboardPartner,
  partnerModalOpen,
  setPartnerModalOpen,
  theme,
  handleRowUpdate,
}: Props) {
  const { postPartner, getPartnerGroupSelectOptions } = usePartner();

  const [posting, setPosting] = useState(false);
  const [partnerGroupSelectOptions, setPartnerGroupSelectOptions] = useState<
    PartnerGroupSelectModel[]
  >([]);

  const [selectedPartnerGroupOption, setSelectedPartnerGroupOption] =
    useState<PartnerGroupSelectModel | null>(null);

  // Load options once (or when modal first opens)
  useEffect(() => {
    if (!partnerModalOpen) return;

    let cancelled = false;

    (async () => {
      if (partnerGroupSelectOptions.length > 0) return;

      const res = await getPartnerGroupSelectOptions();
      if (!cancelled) setPartnerGroupSelectOptions(res);
    })();

    return () => {
      cancelled = true;
    };
  }, [partnerModalOpen, getPartnerGroupSelectOptions, partnerGroupSelectOptions.length]);

  // Initialize selection each time the modal opens / partner changes
  useEffect(() => {
    if (!partnerModalOpen) return;

    setSelectedPartnerGroupOption({
      partnerGroupName: dashboardPartner.partnerGroup?.partnerName,
      partnerGroupId: dashboardPartner.partnerGroup?.id,
    } as PartnerGroupSelectModel);
  }, [partnerModalOpen, dashboardPartner]);

  // Build display options (add program names)
  const displayOptions = useMemo(() => {
    return partnerGroupSelectOptions.map((o) => ({
      ...o,
      partnerGroupName: `${o.partnerGroupName} (${o.programNames})`,
    }));
  }, [partnerGroupSelectOptions]);

  const currentPartnerGroupId = dashboardPartner.partnerGroup?.id ?? null;
  const selectedId = selectedPartnerGroupOption?.partnerGroupId ?? null;

  const canSave = !posting && selectedId != null && selectedId !== currentPartnerGroupId;

  const handleClose = useCallback(() => {
    if (posting) return; // optional: prevent close while saving
    setPartnerModalOpen(false);
  }, [posting, setPartnerModalOpen]);

  const updatePartnerGroup = useCallback(async () => {
    if (!selectedPartnerGroupOption?.partnerGroupId) return;

    const updatedPartnerGroupId = selectedPartnerGroupOption.partnerGroupId;

    const partnerInfo: PartnerProps = {
      ...(dashboardPartner as unknown as PartnerProps),
      partnerGroupId: updatedPartnerGroupId,
    };

    try {
      setPosting(true);

      await postPartner(partnerInfo);

      const selectedOption = partnerGroupSelectOptions.find(
        (x) => x.partnerGroupId === updatedPartnerGroupId
      );

      const updatedPartner: PartnerDashboardProps = {
        ...dashboardPartner,
        partnerGroupName: selectedOption?.partnerGroupName ?? '',
      };

      handleRowUpdate(updatedPartner);
      setPartnerModalOpen(false);
    } finally {
      setPosting(false);
    }
  }, [
    dashboardPartner,
    handleRowUpdate,
    partnerGroupSelectOptions,
    postPartner,
    selectedPartnerGroupOption,
    setPartnerModalOpen,
  ]);

  return (
    <Dialog open={partnerModalOpen} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitleWithClose onClose={handleClose}>Re-Assign User</DialogTitleWithClose>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='overline2'>Partner</Typography>
          <Typography color='textSecondary'>{dashboardPartner?.partnerName}</Typography>
        </Box>

        <Autocomplete
          id='partner-select'
          value={selectedPartnerGroupOption}
          onChange={(_, option) => setSelectedPartnerGroupOption(option)}
          options={displayOptions}
          loading={partnerGroupSelectOptions.length === 0}
          getOptionLabel={(option) => option.partnerGroupName ?? ''}
          isOptionEqualToValue={(option, value) => option.partnerGroupId === value.partnerGroupId}
          renderInput={(params) => <TextField {...params} label='Partner Group' />}
        />
      </DialogContent>

      <DialogActions>
        <Button variant='secondary' size='large' onClick={handleClose} disabled={posting}>
          Cancel
        </Button>

        <Button variant='primary' size='large' onClick={updatePartnerGroup} disabled={!canSave}>
          {posting ? (
            <>
              <span>Updating ... </span>
              <CircularProgress
                style={{ color: theme.palette.grey[500], marginLeft: '5px' }}
                size='16px'
              />
            </>
          ) : (
            'Update Partner Group'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
